import type { RequestToggleCompletionForm } from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue.ts";
import {
  invalidateStatusChangeQueries,
  scheduleQueueAutomationRefetch,
} from "@/features/requests/utils/requestInvalidation";
import {
  assignableDesignersKeys,
  invalidateQueryKeys,
  requestKeys,
} from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import apiClient from "@/lib/api/client";

export function useToggleCompletion() {
  const { data: request } = useGetRequest();
  const requestId = request?.id;
  const queryClient = useQueryClient();
  const { isPending, mutate: submitFeedback } = useMutation({
    mutationFn: async (data: RequestToggleCompletionForm) =>
      apiClient
        .url(`/requests/${requestId}/toggle-completion`)
        .post(data)
        .json(),
  });

  const assignationKeys = requestId
    ? [
        requestKeys.requestById(requestId),
        assignableDesignersKeys.assignableDesignersByRequestId(requestId),
      ]
    : [];

  const invalidateAllRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: requestKeys.all });
    invalidateQueryKeys(queryClient, assignationKeys);
    invalidateStatusChangeQueries(queryClient);
    scheduleQueueAutomationRefetch(queryClient, assignationKeys);
  };

  const completeRequest = (
    data: RequestToggleCompletionForm,
    onClose: () => void,
  ) => {
    const withRating = !!data.rating;

    submitFeedback(data, {
      onError: () => {
        toast.add({
          description: "Please try again later",
          timeout: 2000,
          title: withRating
            ? "Error submitting feedback"
            : "Error completing request",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: withRating
            ? "Thank you for your feedback! We will review it."
            : "The request has been marked as completed.",
          timeout: 2000,
          title: withRating
            ? "Feedback submitted successfully"
            : "Request completed",
          type: "success",
        });
        onClose();
        invalidateAllRelatedQueries();
      },
    });
  };

  const reopenRequest = () => {
    submitFeedback(
      { action: "reopen" },
      {
        onError: () => {
          toast.add({
            description: "Please try again later",
            timeout: 2000,
            title: "Error reopening request",
            type: "error",
          });
        },
        onSuccess: () => {
          toast.add({
            description: "Request has been reopened successfully.",
            timeout: 2000,
            title: "Request reopened",
            type: "success",
          });
          invalidateAllRelatedQueries();
        },
      },
    );
  };

  return {
    completeRequest,
    isPending,
    reopenRequest,
  };
}
